// components/portal/BrandGeoFolders.tsx
// Tenants page as a geographic folder browser: Region (continent) → Country → brands. Each folder
// counts the distinct brands operating there (from /api/inventory/brands/geo-buckets); opening a
// country renders the shared BrandsTable scoped to it.
'use client';

import { useEffect, useMemo, useState } from 'react';
import { Folder, Loader2 } from '@/components/ui/lucide-memo';
import { useKernelStore } from '@/kernel/kernelStores';
import { CONTINENT_ORDER, type Continent } from '#lib/leasing/country-continent';
import { BrandsTable } from '@/components/entities/tables/brands/BrandsTable';
import { Breadcrumb, FolderGrid, FolderCard, FolderSearch } from './folderBrowserUI';

interface GeoBuckets {
  continents: Array<{ continent: Continent; count: number; countryCount: number }>;
  countries: Array<{ country: string; continent: Continent; count: number }>;
}

const CONTINENT_FR: Record<Continent, string> = {
  Europe: 'Europe', 'North America': 'Amérique du Nord', 'South America': 'Amérique du Sud',
  Asia: 'Asie', Africa: 'Afrique', Oceania: 'Océanie', Other: 'Autres',
};

const flagOf = (iso: string) =>
  iso.length === 2 ? iso.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0))) : '🏳️';


export const BrandGeoFolders = () => {
  const lang = useKernelStore((s) => s.language);
  const fr = lang === 'fr';
  const [data, setData] = useState<GeoBuckets | null>(null);
  const [failed, setFailed] = useState(false);
  const [continent, setContinent] = useState<Continent | null>(null);
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/inventory/brands/geo-buckets')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((j) => { if (!cancelled) setData(j as GeoBuckets); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const regionName = (c: Continent) => (fr ? CONTINENT_FR[c] : c);
  const countryName = useMemo(() => {
    let dn: Intl.DisplayNames | null = null;
    try { dn = new Intl.DisplayNames([fr ? 'fr' : 'en'], { type: 'region' }); } catch { dn = null; }
    return (iso: string) => (dn?.of(iso) ?? iso);
  }, [fr]);

  // --- Country leaf: the scoped brands table ---
  if (country) {
    return (
      <div className="flex h-full w-full flex-col">
        <Breadcrumb
          items={[
            { label: fr ? 'Marques' : 'Tenants', onClick: () => { setContinent(null); setCountry(null); } },
            { label: continent ? regionName(continent) : '', onClick: () => setCountry(null) },
            { label: `${flagOf(country)} ${countryName(country)}` },
          ]}
        />
        <div className="min-h-0 flex-1">
          <BrandsTable
            key={country}
            lockedFilters={{ country }}
            assetLedgerTitle={`${countryName(country)}`}
            assetLedgerBreadcrumb={`${fr ? 'Marques' : 'Tenants'} / ${continent ? regionName(continent) : ''}`}
          />
        </div>
      </div>
    );
  }

  if (!data && !failed) {
    return (
      <div className="flex h-full w-full items-center justify-center gap-2 text-[13px] text-[#8A8A8A]">
        <Loader2 size={16} className="animate-spin" /> {fr ? 'Chargement des dossiers…' : 'Loading folders…'}
      </div>
    );
  }
  if (failed || !data) {
    return <div className="flex h-full w-full items-center justify-center text-[13px] text-[#8A8A8A]">{fr ? 'Indisponible.' : 'Unavailable.'}</div>;
  }

  // --- Continent selected: its country folders ---
  if (continent) {
    const countries = data.countries.filter((c) => c.continent === continent);
    return (
      <div className="flex h-full w-full flex-col">
        <Breadcrumb
          items={[
            { label: fr ? 'Marques' : 'Tenants', onClick: () => setContinent(null) },
            { label: regionName(continent) },
          ]}
        />
        <FolderGrid>
          {countries.map((c) => (
            <FolderCard
              key={c.country}
              emoji={flagOf(c.country)}
              title={countryName(c.country)}
              subtitle={`${c.count.toLocaleString(fr ? 'fr-FR' : 'en-US')} ${fr ? 'marques' : 'brands'}`}
              onClick={() => setCountry(c.country)}
            />
          ))}
        </FolderGrid>
      </div>
    );
  }

  // --- Root: continent folders ---
  const continents = [...data.continents].sort(
    (a, b) => CONTINENT_ORDER.indexOf(a.continent) - CONTINENT_ORDER.indexOf(b.continent),
  );
  const trimmedQuery = query.trim();
  return (
    <div className="flex h-full w-full flex-col">
      <Breadcrumb items={[{ label: fr ? 'Marques par région' : 'Tenants by region' }]} />
      <FolderSearch
        value={query}
        onChange={setQuery}
        placeholder={fr ? 'Rechercher une enseigne…' : 'Search for a tenant…'}
      />
      {trimmedQuery ? (
        <div className="min-h-0 flex-1">
          <BrandsTable key={`search:${trimmedQuery}`} initialFilters={{ search: trimmedQuery }} hideSearch />
        </div>
      ) : (
      <FolderGrid>
        {continents.map((c) => (
          <FolderCard
            key={c.continent}
            icon={<Folder size={22} className="text-[#7A7A7A] transition-colors group-hover:text-[#9A9A9A]" />}
            title={regionName(c.continent)}
            subtitle={`${c.count.toLocaleString(fr ? 'fr-FR' : 'en-US')} ${fr ? 'marques' : 'brands'} · ${c.countryCount} ${fr ? 'pays' : 'countries'}`}
            onClick={() => setContinent(c.continent)}
          />
        ))}
      </FolderGrid>
      )}
    </div>
  );
};

BrandGeoFolders.displayName = 'BrandGeoFolders';
